import { useMemo, useState } from 'react';
import { motion } from 'motion/react';
import { ArrowLeft, Plus, VolumeX, X } from 'lucide-react';
import { ScrollArea } from './ui/scroll-area';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useUserData } from './UserDataContext';
import { ParallaxBackground } from './utils/parallax';
import {
  MAX_MUTED_WORDS,
  MIN_MUTED_WORD_LENGTH,
  isCommonWord,
  validateMutedWord,
} from '../utils/mutedWords';

interface MutedWordsScreenProps {
  onBack: () => void;
}

export function MutedWordsScreen({ onBack }: MutedWordsScreenProps) {
  const { mutedWords, muteWord, unmuteWord } = useUserData();
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Set when the pending word is one that would hide most of the feed. The next
  // press confirms — a warning rather than a block, since it's still their call.
  const [confirmCommon, setConfirmCommon] = useState<string | null>(null);

  const sorted = useMemo(
    () => [...(mutedWords || [])].sort((a, b) => a.localeCompare(b)),
    [mutedWords],
  );

  const handleAdd = async () => {
    const problem = validateMutedWord(draft, mutedWords || []);
    if (problem) {
      setError(problem);
      setConfirmCommon(null);
      return;
    }
    if (isCommonWord(draft) && confirmCommon !== draft.trim()) {
      setConfirmCommon(draft.trim());
      setError(null);
      return;
    }
    setError(null);
    setConfirmCommon(null);
    const word = draft;
    setDraft('');
    await muteWord(word);
  };

  return (
    <div className="w-full h-screen bg-light-pattern dark:bg-[#0D0D0D] relative">
      <div className="absolute inset-0 pointer-events-none">
        <ParallaxBackground speed={0.05} />
      </div>

      <div className="w-full max-w-2xl mx-auto h-full flex flex-col relative z-10">
        {/* Header — matches BlockedUsersScreen and the other safety screens:
            same safe-area top padding, same width cap, same divider. */}
        <div
          className="pb-4 px-4 border-b border-black/10 dark:border-white/10 bg-light-pattern dark:bg-[#121212]"
          style={{ paddingTop: 'max(60px, calc(env(safe-area-inset-top) + 24px))' }}
        >
          <div className="flex items-center gap-4 mb-4">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={onBack}
              aria-label="Go back"
              className="text-black dark:text-[#E5E5E5] hover:text-[#F39C12] dark:hover:text-[#4478ea]"
            >
              <ArrowLeft className="w-6 h-6" />
            </motion.button>
            <div>
              <h1 className="text-2xl text-black dark:text-white">Muted Words</h1>
              <p className="text-sm text-[#666666] dark:text-[#a0a0a0]">
                {sorted.length} {sorted.length === 1 ? 'word' : 'words'} muted
              </p>
            </div>
          </div>

          {/* Add form sits inside the header block, where the sibling screens
              put their search bar, so it stays above the divider. */}
          <div className="flex gap-2">
            <Input
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                if (error) setError(null);
                if (confirmCommon) setConfirmCommon(null);
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
              placeholder="Add a word or phrase"
              aria-label="Word or phrase to mute"
              maxLength={60}
              className="flex-1"
            />
            <Button
              onClick={handleAdd}
              disabled={!draft.trim() || (mutedWords?.length || 0) >= MAX_MUTED_WORDS}
              className="bg-brand-gradient text-white px-4 rounded-xl font-bold active:scale-95 transition-transform"
              aria-label="Mute this word"
            >
              <Plus className="w-5 h-5" />
            </Button>
          </div>

          {error ? (
            <p className="text-xs text-red-500 mt-2">{error}</p>
          ) : confirmCommon ? (
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
              “{confirmCommon}” is a very common word. Muting it will hide most of
              your feed. Press add again to confirm.
            </p>
          ) : (
            <p className="text-xs text-[#666666] dark:text-[#a0a0a0] mt-2">
              Whole words only, so muting “gold” won’t hide “golden”. Phrases are
              allowed. At least {MIN_MUTED_WORD_LENGTH} characters.
            </p>
          )}

          <p className="text-[11px] text-[#999999] dark:text-[#777777] mt-1">
            {sorted.length}/{MAX_MUTED_WORDS} muted
          </p>
        </div>

        {/* List */}
        <ScrollArea className="flex-1" style={{ minHeight: 0 }}>
          <div className="p-4">
          {sorted.length === 0 ? (
            <div className="flex flex-col items-center justify-center text-center pt-16 px-6">
              <div className="w-16 h-16 rounded-full bg-black/5 dark:bg-white/5 flex items-center justify-center mb-4">
                <VolumeX className="w-7 h-7 text-[#999999] dark:text-[#666666]" />
              </div>
              <p className="text-black dark:text-white mb-1">Nothing muted yet</p>
              <p className="text-sm text-[#666666] dark:text-[#a0a0a0]">
                Muted words hide matching yaps across your feeds, and keep matching
                forums and tribes out of Discover and search.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {sorted.map((word) => (
                <motion.div
                  key={word}
                  layout
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center justify-between gap-3 rounded-xl px-4 py-3 bg-black/5 dark:bg-white/5"
                >
                  <span className="text-black dark:text-white break-all">{word}</span>
                  <button
                    onClick={() => unmuteWord(word)}
                    aria-label={`Unmute ${word}`}
                    className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center bg-black/5 dark:bg-white/10 active:scale-95 transition-transform"
                  >
                    <X className="w-4 h-4 text-[#666666] dark:text-[#a0a0a0]" />
                  </button>
                </motion.div>
              ))}
            </div>
          )}

          {sorted.length > 0 && (
            <p className="text-xs text-[#999999] dark:text-[#777777] mt-6">
              Groups you have already joined stay accessible even if their name
              matches a muted word. Your own yaps are never hidden from you.
            </p>
          )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}
